import type { FC } from "react";

import { Navigate, type RouteObject } from "src/router";

import { useUser } from "src/contexts/user-context";
import { useOrganizationQuery } from "src/graphql";
import { PageSpinner } from "src/components/loading";
import { DetailPage } from "src/components/layout";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { EntitledRoute } from "src/components/router/entitled-route";
import { useHasSupportAccessControl } from "src/hooks/use-has-support-access-control";
import { useHasOrganizationSettings } from "src/hooks/use-has-organization-settings";
import { useAgentsOptOutEnabled } from "src/pages/agents/hooks/use-agents-opt-out-enabled";
import { lazyNamed } from "src/utils/lazy-named";
import {
  OrganizationOutlet,
  useOrganizationOutletContextIfAvailable,
} from "./outlet";

const Roles = lazyNamed(() => import("./roles"), "Roles");
const NewRole = lazyNamed(() => import("./roles/new"), "NewRole");
const RoleUsers = lazyNamed(() => import("./roles/role/users"), "RoleUsers");
const RoleWorkspaceGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace"),
  "RoleWorkspacePolicy",
);
const RoleWorkspaces = lazyNamed(
  () => import("./roles/role/workspaces"),
  "RoleWorkspaces",
);
const RoleApiKeys = lazyNamed(
  () => import("./roles/role/api-keys"),
  "RoleApiKeys",
);
const Users = lazyNamed(() => import("./users"), "Users");
const ViewUser = lazyNamed(() => import("./users/view"), "ViewUser");
const DestinationGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/destinations"),
  "DestinationGrants",
);
const SourceGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/sources"),
  "SourceGrants",
);
const ParentModelGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/parent-models"),
  "ParentModelGrants",
);
const ViewRole = lazyNamed(() => import("./roles/role"), "ViewRole");
const AddUsers = lazyNamed(() => import("./roles/role/users/add"), "AddUsers");
const InviteUserModal = lazyNamed(
  () => import("./users/invite"),
  "InviteUserModal",
);
const GeneralGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/general"),
  "GeneralGrants",
);
const AgentsGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/agents"),
  "AgentsGrants",
);
const DecisionEngineGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/decision-engines"),
  "DecisionEngineGrants",
);
const CepConsentGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/cep-consent"),
  "CepConsentGrants",
);
const NativeDeliveryGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/native-delivery"),
  "NativeDeliveryGrants",
);
const SpaceGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/spaces"),
  "SpaceGrants",
);
const EventsGrants = lazyNamed(
  () => import("./roles/role/workspaces/workspace/events"),
  "EventsGrants",
);
const SSOSetup = lazyNamed(() => import("./sso"), "SSOSetup");
const SSOGroupMapping = lazyNamed(
  () => import("./sso/groups"),
  "SSOGroupMapping",
);
const Organization = lazyNamed(() => import("./organization"), "Organization");
const ManageSupportAccess = lazyNamed(
  () => import("src/components/support-access"),
  "ManageSupportAccess",
);
const Billing = lazyNamed(
  () => import("src/pages/organizations/billing"),
  "Billing",
);
const AiFeaturesSettings = lazyNamed(
  () => import("./ai-features"),
  "AiFeaturesSettings",
);

const AiFeaturesSettingsRoute: FC = () => {
  const isAgentsOptOutEnabled = useAgentsOptOutEnabled();

  if (!isAgentsOptOutEnabled) {
    return <Navigate to="/organization/settings" replace />;
  }

  return <AiFeaturesSettings />;
};

const Loader: FC = () => {
  const { workspace } = useUser();
  const id = workspace?.organization?.id?.toString();
  const { hasAccess, isLoading: isLoadingOrgSettings } =
    useHasOrganizationSettings();

  const { data: organization, isLoading: isLoadingOrganization } =
    useOrganizationQuery(
      { id: id || "" },
      { enabled: Boolean(id), select: (data) => data.organizations_by_pk },
    );

  if (isLoadingOrgSettings || isLoadingOrganization) {
    return <PageSpinner />;
  }

  if (!hasAccess || !organization) {
    return <Navigate to="/settings" replace />;
  }

  return <OrganizationOutlet context={{ organization }} />;
};

export const Layout: FC = () => {
  const { organization } = useOrganizationOutletContextIfAvailable() ?? {};
  const { permission: userSupportAccessPermission } =
    useHasSupportAccessControl();
  const isAgentsOptOutEnabled = useAgentsOptOutEnabled();

  const isSsoEnabled = (organization?.auth0_connections || []).length > 0;

  return (
    <DetailPage
      title="Organization settings"
      heading="Organization settings"
      tabDepth={3}
      tabs={[
        { title: "General", path: "settings", variant: "form" },
        {
          title: "Billing",
          path: "billing",
          variant: "fullPage",
        },
        {
          title: "Users",
          path: "members",
          variant: "table",
        },
        {
          title: "Groups",
          path: "groups",
          variant: "fullPage",
        },
        { title: "Single sign-on", path: "sso", variant: "form" },
        {
          title: "AI features",
          path: "ai-features",
          isHidden: !isAgentsOptOutEnabled,
          variant: "form",
        },
        {
          title: "SSO mappings",
          path: "sso-mappings",
          isHidden: !isSsoEnabled,
          variant: "form",
        },
        {
          title: "Support access",
          path: "support-access",
          isHidden: !userSupportAccessPermission,
          variant: "form",
        },
      ]}
    >
      {organization && <OrganizationOutlet context={{ organization }} />}
    </DetailPage>
  );
};

export const routes: Array<RouteObject> = [
  {
    path: "organization/*",
    element: <Loader />,
    spacesAccess: "hidden-from-limited-view",
    children: [
      {
        path: "",
        element: <Layout />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: <Navigate to="members" replace />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "settings",
            element: <Organization />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "ai-features",
            element: <AiFeaturesSettingsRoute />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "members",
            element: <Users />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "invite",
                element: <InviteUserModal />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":userId",
                element: <ViewUser />,
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
          {
            path: "groups",
            element: <Roles />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "new",
                element: <NewRole />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":roleId",
                element: <ViewRole />,
                spacesAccess: "hidden-from-limited-view",
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate
                        to={{ pathname: "workspaces", search: location.search }}
                        replace
                      />
                    ),
                    spacesAccess: "hidden-from-limited-view",
                  },
                  {
                    path: "members",
                    element: <RoleUsers />,
                    spacesAccess: "hidden-from-limited-view",
                    children: [
                      {
                        path: "new",
                        element: <AddUsers />,
                        spacesAccess: "hidden-from-limited-view",
                      },
                    ],
                  },
                  {
                    path: "api-keys",
                    element: (
                      <FeatureFlagRoute
                        flagKey="apiKeysUserGroupIDs"
                        redirect="../workspaces"
                      >
                        <RoleApiKeys />
                      </FeatureFlagRoute>
                    ),
                    spacesAccess: "hidden-from-limited-view",
                  },
                  {
                    path: "workspaces",
                    element: <RoleWorkspaces />,
                    spacesAccess: "hidden-from-limited-view",
                    children: [
                      {
                        path: ":workspaceId",
                        element: <RoleWorkspaceGrants />,
                        spacesAccess: "hidden-from-limited-view",
                        children: [
                          {
                            index: true,
                            element: (
                              <Navigate
                                to={{
                                  pathname: "general",
                                  search: location.search,
                                }}
                                replace
                              />
                            ),
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "general",
                            element: <GeneralGrants />,
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "spaces",
                            element: <SpaceGrants />,
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "sources",
                            element: <SourceGrants />,
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "destinations",
                            element: <DestinationGrants />,
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "parent-models",
                            element: <ParentModelGrants />,
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "agents",
                            element: <AgentsGrants />,
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "ai-decisioning",
                            element: (
                              <EntitledRoute
                                entitlement="decisionEngine"
                                redirect="general"
                              >
                                <DecisionEngineGrants />
                              </EntitledRoute>
                            ),
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "cep-consent",
                            element: (
                              <EntitledRoute
                                entitlement="cep"
                                redirect="general"
                              >
                                <CepConsentGrants />
                              </EntitledRoute>
                            ),
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "native-delivery",
                            element: (
                              <EntitledRoute
                                entitlement="cep"
                                redirect="general"
                              >
                                <NativeDeliveryGrants />
                              </EntitledRoute>
                            ),
                            spacesAccess: "hidden-from-limited-view",
                          },
                          {
                            path: "events/*",
                            element: (
                              <FeatureFlagRoute
                                flagKey="eventsRbacEnabled"
                                redirect="general"
                              >
                                <EventsGrants />
                              </FeatureFlagRoute>
                            ),
                            spacesAccess: "hidden-from-limited-view",
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "sso",
            element: <SSOSetup />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "sso-mappings",
            element: <SSOGroupMapping />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "support-access",
            element: <ManageSupportAccess level="organization" />,
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "billing",
            element: <Billing />,
            spacesAccess: "hidden-from-limited-view",
          },
        ],
      },
    ],
  },
];
